<template>
    <div>
        <div class="d-flex align-items-center mb-2" style="gap: 8px;">
            <div style="min-width: 260px; max-width: 420px; flex: 1;">
                <multiselect v-model="selectedStateFilter" :options="addableStateOptions" label="name"
                             track-by="abbreviation" :close-on-select="false" :clear-on-select="false"
                             :preserve-search="true" placeholder="Add state(s) to the matrix..." multiple>
                    <template #option="{ option }">
                        <span class="text-muted mr-1">{{ option.abbreviation }}</span>
                        {{ option.name }}
                    </template>
                    <template #tag="{ option, remove }">
                        <span class="multiselect__tag compact-tag"><span class="compact-tag__label">{{ option.abbreviation }}</span><i class="multiselect__tag-icon" @mousedown.prevent.stop @click.stop="remove(option)" /></span>
                    </template>
                </multiselect>
            </div>
            <b-button v-if="selectedStateFilter.length" size="sm" variant="secondary"
                      class="clear-added-btn" @click="selectedStateFilter = []">
                <i class="fa fa-times mr-1" />Clear added
            </b-button>
            <b-dropdown size="sm" variant="outline-primary" text="Copy state to..." :disabled="!canCopy"
                        no-close-on-click>
                <b-dropdown-form style="min-width: 360px;">
                    <b-form-group label="Source state" label-size="sm" class="mb-2">
                        <b-form-select v-model="copySource" size="sm" :options="sourceStateOptions" />
                    </b-form-group>
                    <b-form-group label="Target states" label-size="sm" class="mb-2">
                        <multiselect v-model="copyTargets" :options="copyTargetOptions" label="name"
                                     track-by="abbreviation" :close-on-select="false" :clear-on-select="false"
                                     :preserve-search="true" placeholder="Leave empty = all states (except source)"
                                     multiple>
                            <template #option="{ option }">
                                <span class="text-muted mr-1">{{ option.abbreviation }}</span>
                                {{ option.name }}
                            </template>
                            <template #tag="{ option, remove }">
                                <span class="multiselect__tag compact-tag"><span class="compact-tag__label">{{ option.abbreviation }}</span><i class="multiselect__tag-icon" @mousedown.prevent.stop @click.stop="remove(option)" /></span>
                            </template>
                        </multiselect>
                    </b-form-group>
                    <div class="d-flex align-items-center" style="gap: 6px;">
                        <b-button size="sm" variant="primary" :disabled="!copySource" @click="copyToAll">
                            <i class="fa fa-copy mr-1" />Copy
                        </b-button>
                        <b-button size="sm" variant="outline-primary" :disabled="!copySource" @click="copyToEmpty">
                            <i class="fa fa-copy mr-1" />Copy to empty only
                        </b-button>
                    </div>
                </b-dropdown-form>
            </b-dropdown>
        </div>
        <b-table-simple small bordered responsive class="dea-schedule-table">
            <thead>
                <tr>
                    <th style="min-width: 180px;">State</th>
                    <th v-for="c in classes" :key="c.value">{{ c.label }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td><strong>Default (DEF)</strong></td>
                    <td v-for="c in classes" :key="c.value">
                        <b-input type="number" min="0" :value="getValue(DEFAULT_STATE_KEY, c.value)"
                                 @input="setValue(DEFAULT_STATE_KEY, c.value, $event)" size="sm" />
                    </td>
                </tr>
                <tr v-for="s in filteredStates" :key="s.abbreviation">
                    <td>
                        <span class="text-muted mr-1">{{ s.abbreviation }}</span>
                        {{ s.name }}
                    </td>
                    <td v-for="c in classes" :key="c.value">
                        <b-input type="number" min="0" :value="getValue(s.abbreviation, c.value)"
                                 @input="setValue(s.abbreviation, c.value, $event)" size="sm" />
                    </td>
                </tr>
            </tbody>
        </b-table-simple>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import {
    DeaScheduleExpirationEntry,
    DEA_SCHEDULE_CLASSES,
    DEFAULT_STATE_KEY,
} from "@/models/DeaScheduleExpirationEntry";
import { ValidValues } from "@/data/ValidValues";
import Multiselect from "vue-multiselect";

@Component({ name: "DeaScheduleExpirationField", components: { Multiselect } })
export default class DeaScheduleExpirationField extends Vue {
    @Prop({ type: Array, default: () => [] }) value!: DeaScheduleExpirationEntry[];

    selectedStateFilter: ValidValues.State[] = [];
    copySource: string | null = null;
    copyTargets: ValidValues.State[] = [];
    readonly DEFAULT_STATE_KEY = DEFAULT_STATE_KEY;

    get classes() {
        return DEA_SCHEDULE_CLASSES;
    }

    get states() {
        return ValidValues.statesData;
    }

    get statesWithValues(): Set<string> {
        return new Set(this.value
                           .map(e => e.state)
                           .filter(s => s !== DEFAULT_STATE_KEY));
    }

    get filteredStates() {
        const selected = new Set(this.selectedStateFilter.map(s => s.abbreviation));
        return this.states.filter(s =>
            this.statesWithValues.has(s.abbreviation) || selected.has(s.abbreviation));
    }

    /** States that do not yet have any entries — these are the ones the user can "add" via multiselect. */
    get addableStateOptions() {
        return this.states.filter(s => !this.statesWithValues.has(s.abbreviation));
    }

    get sourceStateOptions() {
        const options = this.states.map(s => ({ value: s.abbreviation, text: `${s.abbreviation} — ${s.name}` }));
        options.unshift({ value: DEFAULT_STATE_KEY, text: "Default (DEF)" });
        return [{ value: null, text: "Choose a state…" }, ...options];
    }

    /** Target multiselect options — all real states except the chosen source. */
    get copyTargetOptions() {
        return this.states.filter(s => s.abbreviation !== this.copySource);
    }

    get canCopy(): boolean {
        return this.value.length > 0;
    }

    getEntry(state: string, deaClass: number): DeaScheduleExpirationEntry | undefined {
        return this.value.find(e => e.state === state && e.deaScheduleDrug === deaClass);
    }

    getValue(state: string, deaClass: number): number | "" {
        const entry = this.getEntry(state, deaClass);
        return entry ? entry.days : "";
    }

    setValue(state: string, deaClass: number, raw: string | number) {
        const parsed = raw === "" || raw === null || raw === undefined ? NaN : Number(raw);
        const isValidNumber = !Number.isNaN(parsed) && parsed >= 0;

        // Clearing the DEF row is disallowed — a missing DEF breaks all lookups for that class.
        // For any other state, clearing removes the entry so it falls back to DEF at runtime.
        if (!isValidNumber && state === DEFAULT_STATE_KEY) return;

        const next = this.value.filter(e => !(e.state === state && e.deaScheduleDrug === deaClass));
        if (isValidNumber) {
            next.push({ state, deaScheduleDrug: deaClass, days: Math.floor(parsed) });
            // Once a value exists for this state, drop it from the "added" filter so the chip UI
            // doesn't double-count (the state now shows automatically because it has data).
            this.selectedStateFilter = this.selectedStateFilter.filter(s => s.abbreviation !== state);
        }
        this.$emit("input", next);
    }

    copyToAll() {
        this.applyCopy(false);
    }

    copyToEmpty() {
        this.applyCopy(true);
    }

    private applyCopy(onlyEmpty: boolean) {
        if (!this.copySource) return;
        const sourceEntries = this.value.filter(e => e.state === this.copySource);
        if (sourceEntries.length === 0) return;

        // Targets: explicit selection if provided, otherwise every real state. Source is always excluded.
        const targets = (this.copyTargets.length > 0
                            ? this.copyTargets.map(s => s.abbreviation)
                            : this.states.map(s => s.abbreviation))
                        .filter(s => s !== this.copySource);
        const next = [...this.value];

        for (const target of targets) {
            for (const cls of this.classes.map(c => c.value)) {
                const src = sourceEntries.find(e => e.deaScheduleDrug === cls);
                const existingIdx = next.findIndex(e => e.state === target && e.deaScheduleDrug === cls);

                if (onlyEmpty) {
                    // Fill blanks only — don't touch cells that already have a value.
                    if (existingIdx === -1 && src) {
                        next.push({ state: target, deaScheduleDrug: cls, days: src.days });
                    }
                    continue;
                }

                // Full copy: target should mirror source. If source has a value/noval, write it
                if (src) {
                    if (existingIdx !== -1) {
                        next[existingIdx] = { ...next[existingIdx], days: src.days };
                    } else {
                        next.push({ state: target, deaScheduleDrug: cls, days: src.days });
                    }
                } else if (existingIdx !== -1) {
                    next.splice(existingIdx, 1);
                }
            }
        }

        this.$emit("input", next);
    }
}
</script>

<style scoped lang="scss">
.dea-schedule-table {
    font-size: 0.9rem;

    ::v-deep(th),
    ::v-deep(td) {
        padding: 3px 6px !important;
        vertical-align: middle;
    }

    ::v-deep(input) {
        min-width: 70px;
    }
}

.clear-added-btn {
    white-space: nowrap;
    flex-shrink: 0;
}


::v-deep(.multiselect) {
    &.multiselect--active {
        z-index: 1060;
    }
}
::v-deep(.multiselect__content-wrapper) {
    z-index: 1060;
}

::v-deep(.multiselect__tag.compact-tag) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    margin-right: 4px;
    margin-bottom: 3px;
    line-height: 1;
    font-size: 0.85rem;

    .compact-tag__label {
        font-weight: 600;
        letter-spacing: 0.3px;
    }

    .multiselect__tag-icon {
        position: static;
        width: auto;
        height: auto;
        margin: 0;
        padding: 0 1px;
        line-height: 1;
        font-size: 14px;

        &::after {
            content: "×";
            color: inherit;
            font-size: inherit;
            line-height: 1;
        }

        &:hover,
        &:focus {
            background: transparent;
        }
    }
}
</style>
